"use client";

import { useSignIn } from "@clerk/nextjs";
import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";
import { toast } from "@/components/ui/sonner";
import { isClerkApiResponseError } from "@/features/auth/lib/clerk-errors";
import { useResendCooldown } from "./use-resend-cooldown";
import { useSessionNavigate } from "./use-session-navigate";

type UsePasswordResetActionsParams = {
  redirectUrl: string | null;
  onSignedIn?: () => void;
  setIsSubmitting: (value: boolean) => void;
  setGlobalError: (key: string | null) => void;
  /** The reset code has been sent — move on to the code step. */
  onCodeSent: () => void;
  /** The code checked out — move on to the new password step. */
  onCodeVerified: () => void;
};

/**
 * Password reset on top of the existing sign-in attempt: Clerk sends a code to
 * the account's e-mail, and a verified code unlocks setting a new password.
 */
export const usePasswordResetActions = ({
  redirectUrl,
  onSignedIn,
  setIsSubmitting,
  setGlobalError,
  onCodeSent,
  onCodeVerified,
}: UsePasswordResetActionsParams) => {
  const t = useTranslations("auth");
  const { signIn } = useSignIn();
  const navigate = useSessionNavigate({ redirectUrl, onSignedIn });
  const cooldown = useResendCooldown();

  const [codeError, setCodeError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const startPasswordReset = useCallback(async () => {
    setIsSubmitting(true);
    setGlobalError(null);
    setCodeError(null);

    try {
      const { error } = await signIn.resetPasswordEmailCode.sendCode();

      if (error) {
        setGlobalError("auth.genericError");
        return;
      }

      cooldown.start();
      onCodeSent();
    } finally {
      setIsSubmitting(false);
    }
  }, [cooldown, onCodeSent, setGlobalError, setIsSubmitting, signIn]);

  const submitResetCode = useCallback(
    async (code: string) => {
      setIsSubmitting(true);
      setCodeError(null);
      setGlobalError(null);

      try {
        const { error } = await signIn.resetPasswordEmailCode.verifyCode({
          code,
        });

        // A rejected code must not open the new-password step.
        if (error) {
          if (isClerkApiResponseError(error)) {
            setCodeError("auth.verifyCode.validators.invalid");
          } else {
            setGlobalError("auth.genericError");
          }
          return;
        }

        setPasswordError(null);
        onCodeVerified();
      } finally {
        setIsSubmitting(false);
      }
    },
    [onCodeVerified, setGlobalError, setIsSubmitting, signIn],
  );

  const resendResetCode = useCallback(async () => {
    if (cooldown.isCoolingDown) {
      return;
    }

    setCodeError(null);
    setGlobalError(null);

    const { error } = await signIn.resetPasswordEmailCode.sendCode();

    if (error) {
      setGlobalError("auth.genericError");
      return;
    }

    cooldown.start();
  }, [cooldown, setGlobalError, signIn]);

  const submitNewPassword = useCallback(
    async (password: string) => {
      setIsSubmitting(true);
      setPasswordError(null);
      setGlobalError(null);

      try {
        const { error } = await signIn.resetPasswordEmailCode.submitPassword({
          password,
        });

        if (error) {
          if (isClerkApiResponseError(error)) {
            setPasswordError("auth.createPassword.validators.notMet");
          } else {
            setGlobalError("auth.genericError");
          }
          return;
        }

        if (signIn.status !== "complete") {
          setGlobalError("auth.genericError");
          return;
        }

        // Fired before navigating: the Toaster lives in the root layout, so the
        // toast survives the client-side move into the panel.
        toast.success(t("resetPassword.success"));
        await signIn.finalize({ navigate });
      } finally {
        setIsSubmitting(false);
      }
    },
    [navigate, setGlobalError, setIsSubmitting, signIn, t],
  );

  return {
    codeError,
    passwordError,
    resendResetCode,
    resendSecondsLeft: cooldown.secondsLeft,
    startPasswordReset,
    submitNewPassword,
    submitResetCode,
  };
};
